import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { X, ArrowUpRight } from "lucide-react";
import gsap from "gsap";
import BrandLogo from "./BrandLogo";
import SocialIcons from "./SocialIcons";

const LANGS = [
  { code: "en", label: "English" },
  { code: "id", label: "Bahasa" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
];

export default function MobileMenu({ open, onClose, user, onLogout }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const current = LANGS.find((l) => l.code === i18n.language) || LANGS[0];

  const overlayRef = useRef(null);
  const panelRef = useRef(null);
  const linkRefs = useRef([]);
  const tlRef = useRef(null);
  const mountedRef = useRef(false);

  const links = [
    { to: "/", label: "Home" },
    { to: "/tours", label: t("nav.tours") || "Tours" },
    { to: "/blog", label: "Blog" },
    { to: "/about", label: "About" },
    { to: "/consultations", label: "Consultation" },
    { to: "/partner", label: "Partnership" },
  ];
  if (user) links.push({ to: "/dashboard", label: t("nav.dashboard") || "Dashboard" });
  if (user && user.role === "admin")
    links.push({ to: "/admin", label: t("nav.admin") || "Admin" });

  // Lock body scroll
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Animate in / out
  useEffect(() => {
    if (!overlayRef.current || !panelRef.current) return;
    if (tlRef.current) tlRef.current.kill();

    // On first mount when closed, just set initial state without animating.
    if (!mountedRef.current) {
      mountedRef.current = true;
      gsap.set(overlayRef.current, { autoAlpha: 0 });
      gsap.set(panelRef.current, { yPercent: -100 });
      gsap.set(linkRefs.current.filter(Boolean), { opacity: 0, y: 28 });
      if (!open) return;
    }

    if (open) {
      gsap.set(overlayRef.current, { autoAlpha: 0 });
      gsap.set(panelRef.current, { yPercent: -100 });
      gsap.set(linkRefs.current.filter(Boolean), { opacity: 0, y: 28 });

      tlRef.current = gsap
        .timeline()
        .to(overlayRef.current, { autoAlpha: 1, duration: 0.35, ease: "power2.out" })
        .to(
          panelRef.current,
          { yPercent: 0, duration: 0.95, ease: "expo.out" },
          "-=0.2"
        )
        .to(
          linkRefs.current.filter(Boolean),
          {
            opacity: 1,
            y: 0,
            duration: 0.7,
            ease: "power3.out",
            stagger: 0.07,
          },
          "-=0.5"
        );
    } else {
      tlRef.current = gsap
        .timeline()
        .to(linkRefs.current.filter(Boolean), {
          opacity: 0,
          y: -16,
          duration: 0.25,
          ease: "power2.in",
          stagger: 0.03,
        })
        .to(
          panelRef.current,
          { yPercent: -100, duration: 0.7, ease: "expo.inOut" },
          "-=0.1"
        )
        .to(overlayRef.current, { autoAlpha: 0, duration: 0.3 }, "-=0.4");
    }
  }, [open]);

  const handleNav = (to) => {
    onClose();
    setTimeout(() => navigate(to), 350);
  };

  const cycleLang = () => {
    const idx = LANGS.findIndex((l) => l.code === current.code);
    const next = LANGS[(idx + 1) % LANGS.length];
    i18n.changeLanguage(next.code);
  };

  return (
    <>
      {/* Subtle backdrop (visible during slide) */}
      <div
        ref={overlayRef}
        className="fixed inset-0 z-[60] bg-black/30 md:hidden"
        onClick={onClose}
        aria-hidden
      />

      {/* Full-screen panel */}
      <aside
        ref={panelRef}
        data-testid="mobile-menu"
        className="fixed inset-0 z-[70] md:hidden bg-[#FDFBF7] flex flex-col"
        style={{ willChange: "transform" }}
        aria-modal="true"
        role="dialog"
      >
        {/* Header bar */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4">
          <button
            type="button"
            onClick={() => handleNav("/")}
            className="inline-flex"
            aria-label="Home"
          >
            <BrandLogo variant="dark" size="md" asLink={false} />
          </button>
          <button
            data-testid="mobile-menu-close"
            onClick={onClose}
            className="w-11 h-11 -mr-2 rounded-full flex items-center justify-center text-slate-800 hover:bg-stone-100 transition-colors"
            aria-label="Close menu"
          >
            <X size={24} strokeWidth={1.4} />
          </button>
        </div>

        <div className="h-px w-full bg-stone-200/70" />

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-7 pt-12 pb-28">
          {/* Eyebrow */}
          <div
            ref={(el) => (linkRefs.current[0] = el)}
            className="text-[10px] font-semibold tracking-[0.35em] uppercase text-slate-400 mb-10"
          >
            Menu
          </div>

          {/* Big nav links */}
          <nav className="space-y-1">
            {links.map((l, i) => (
              <button
                key={`${l.to}-${i}`}
                ref={(el) => (linkRefs.current[i + 1] = el)}
                onClick={() => handleNav(l.to)}
                data-testid={`mobile-nav-${i}`}
                className="group flex items-baseline justify-between w-full text-left py-4 border-b border-stone-200/70 hover:border-seafoam/60 transition-colors"
              >
                <span className="font-light tracking-tight text-slate-900 group-hover:text-seafoam transition-colors"
                      style={{ fontSize: "clamp(2.25rem, 9vw, 3.5rem)", lineHeight: 1 }}>
                  {l.label}
                </span>
                <ArrowUpRight
                  size={22}
                  strokeWidth={1.3}
                  className="text-slate-300 group-hover:text-seafoam transition-all duration-300 group-hover:rotate-45 shrink-0 ml-4"
                />
              </button>
            ))}
          </nav>

          {/* Reach us */}
          <div
            ref={(el) => (linkRefs.current[links.length + 1] = el)}
            className="mt-14"
          >
            <div className="text-[10px] font-semibold tracking-[0.35em] uppercase text-slate-400 mb-5">
              Reach us
            </div>
            <div className="flex flex-wrap gap-2.5">
              <a
                data-testid="mobile-wa"
                href="https://wa.me/6287777881016?text=Halo%20Across%20Indonesia"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-emerald-50 text-emerald-700 text-sm border border-emerald-100"
              >
                WhatsApp <ArrowUpRight size={13} strokeWidth={1.6} />
              </a>
              <a
                data-testid="mobile-tg"
                href="https://t.me/acrossindonesia"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-sky-50 text-sky-700 text-sm border border-sky-100"
              >
                Telegram <ArrowUpRight size={13} strokeWidth={1.6} />
              </a>
              <a
                href="mailto:win.oktia@gmail.com"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-stone-50 text-slate-700 text-sm border border-stone-200"
              >
                Email <ArrowUpRight size={13} strokeWidth={1.6} />
              </a>
            </div>

            <div className="mt-7">
              <div className="text-[10px] font-semibold tracking-[0.35em] uppercase text-slate-400 mb-3">
                Follow
              </div>
              <SocialIcons
                variant="dark"
                compact={false}
                size={16}
                testidPrefix="mobile-social"
              />
            </div>
          </div>
        </div>

        {/* Sticky footer */}
        <div className="border-t border-stone-200/80 px-6 py-5 bg-[#FDFBF7] flex items-center justify-between gap-4">
          <button
            data-testid="mobile-lang"
            onClick={cycleLang}
            className="text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-500 hover:text-slate-900 transition-colors"
          >
            {current.code} · {current.label}
          </button>
          {user ? (
            <button
              data-testid="mobile-logout"
              onClick={() => { onLogout(); onClose(); }}
              className="btn-pill bg-slate-900 text-white hover:bg-slate-800 text-sm shadow-md"
            >
              {t("nav.logout") || "Sign out"}
            </button>
          ) : (
            <button
              onClick={() => handleNav("/login")}
              data-testid="mobile-sign-in"
              className="btn-pill bg-slate-900 text-white hover:bg-slate-800 text-sm shadow-md"
            >
              Sign In <ArrowUpRight size={14} strokeWidth={1.6} />
            </button>
          )}
        </div>
      </aside>
    </>
  );
}
